import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Button, Input, Modal, Spinner, alert } from 'equasion-lib';
import { FiFileText } from 'react-icons/fi';
import { useRootStore } from '@/lib/providers/RootProvider';
import type { PrintableOrderDocument } from '@/lib/rxPrint';
import downloadFile from '@/lib/utils/downloadFile';
import {
  CERTIFICATE_OF_ORIGIN,
  GENERATE_CERTIFICATE_OF_ORIGIN,
  certificateOfOriginWarehouseInput,
  formatCertificateDate,
  poundsToKilograms,
  showsCertificateOfOrigin
} from '@/lib/certificateOfOrigin';
import './CertificateOfOriginButton.scss';

/** The print key (`sendingKey`) of a certificate sent from this button. */
export const CERTIFICATE_OF_ORIGIN_PRINT_KEY = 'certificate-of-origin';

/** See SalesOrderShipping: Enter would commit an empty number Input as a real 0. */
const suppressEnterCommit = (e) => {
  if (e.key === 'Enter' || e.keyCode === 13) e.preventDefault();
};

type FormProps = {
  orderId: string;
  companyId: string;
  rxPrintEnabled: boolean;
  print: (document: { id: string; link: string; humanFilename?: string | null }) => void;
  onClose: () => void;
  onDone?: () => void;
};

/** The modal body. It owns the query and the mutation, so the pages need neither until a tap. */
const CertificateOfOriginForm = ({
  orderId,
  companyId,
  rxPrintEnabled,
  print,
  onClose,
  onDone
}: FormProps) => {
  const { data, loading, error } = useQuery(CERTIFICATE_OF_ORIGIN, {
    variables: { orderId, companyId },
    fetchPolicy: 'network-only'
  });
  const [generate] = useMutation(GENERATE_CERTIFICATE_OF_ORIGIN);
  const view = data?.salesOrderCertificateOfOrigin;
  const [packages, setPackages] = useState<string | number>('');
  const [weight, setWeight] = useState<string | number>('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!view) return;
    setPackages(view.packageCount ?? '');
    setWeight(view.grossWeightLbs ?? '');
  }, [view]);

  const kilograms = poundsToKilograms(weight);
  const verb = rxPrintEnabled ? 'Print' : 'Download';

  const handleGenerate = async () => {
    const parsed = certificateOfOriginWarehouseInput(packages, weight);
    if (parsed.ok === false) {
      setProblem(parsed.message);
      return;
    }
    setProblem(null);
    setBusy(true);
    try {
      const { data: result } = await generate({
        variables: { orderId, companyId, input: parsed.input }
      });
      const document = result?.salesOrderCertificateOfOriginGenerate;
      onDone?.();
      onClose();
      if (document?.id && document?.link) print(document);
    } catch (e: any) {
      alert('Could not generate the certificate', e?.message || 'Try again.', 6, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !view) return <Spinner />;
  if (error && !view) {
    return (
      <div className="certificate-of-origin-modal__body">
        <strong>Certificate of Origin</strong>
        <p>{error.message || 'Could not load the certificate.'}</p>
        <Button type="secondary" fullWidth {...{ onClick: onClose }}>
          Close
        </Button>
      </div>
    );
  }

  const current = view?.document;
  return (
    <div className="certificate-of-origin-modal__body">
      <strong>Certificate of Origin</strong>
      {view && !view.available ? (
        <p className="certificate-of-origin-modal__problem">{view.unavailableReason}</p>
      ) : (
        <p>
          For SO #{view?.vendorSalesOrderNumber} to {view?.consigneeName || 'Puerto Rico'}
          {view?.carrierName ? `, via ${view.carrierName}` : ''}, shipped{' '}
          {formatCertificateDate(view?.shipDate)}. Enter the packages and their total weight; the
          form prints it in kilos.
        </p>
      )}
      <Input
        form={false}
        manual
        onKeyDown={suppressEnterCommit}
        label="Packages, boxes or cases"
        inputType="number"
        value={packages}
        onChange={({ value }) => setPackages(value)}
      />
      <Input
        form={false}
        manual
        onKeyDown={suppressEnterCommit}
        label="Gross weight (lbs)"
        inputType="number"
        value={weight}
        onChange={({ value }) => setWeight(value)}
      />
      <p className="certificate-of-origin-modal__kilos">
        {kilograms !== null ? `${kilograms.toFixed(2)} kg on the certificate` : 'No weight yet'}
      </p>
      {problem && <p className="certificate-of-origin-modal__problem">{problem}</p>}
      <div>
        <Button
          type="primary"
          fullWidth
          disabled={busy || !view?.available}
          {...{ onClick: handleGenerate }}
        >
          {busy ? 'Generating…' : `Generate & ${verb}`}
        </Button>
        {current?.id && current?.link && (
          <Button
            type="secondary"
            fullWidth
            disabled={busy}
            {...{
              onClick: () => {
                onClose();
                print(current);
              }
            }}
          >
            {`${verb} Current Certificate`}
          </Button>
        )}
        <Button type="secondary" fullWidth disabled={busy} {...{ onClick: onClose }}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * "Certificate of Origin" for an order shipping to Puerto Rico, at Shipping or later: generate the
 * certificate with the packages and gross weight (the rest comes from the order), or reprint the
 * current one. With RxPrint on it prints through the page's sender (anything not sent is
 * downloaded); otherwise it downloads. Renders nothing for any other order.
 */
const CertificateOfOriginButton = ({
  orderId,
  vendorStatus,
  shipsToPuertoRico,
  rxPrintEnabled,
  sendingKey = null,
  onSendToPrinter,
  label,
  className,
  onDone
}: {
  orderId?: string | null;
  vendorStatus?: string | null;
  shipsToPuertoRico?: boolean | null;
  rxPrintEnabled: boolean;
  sendingKey?: string | null;
  onSendToPrinter?: (key: string, documents: PrintableOrderDocument[]) => void;
  /** Overrides the button text (e.g. "Regenerate Certificate of Origin" beside a print button). */
  label?: string;
  className?: string;
  /** After a certificate is generated — e.g. refetch the order's documents. */
  onDone?: () => void;
}) => {
  const selectedCompany = useRootStore((state) => state.selectedCompany) as any;
  const companyId = selectedCompany?.id;
  const [open, setOpen] = useState(false);

  if (!orderId || !companyId || !showsCertificateOfOrigin({ shipsToPuertoRico, vendorStatus })) {
    return null;
  }

  const print = (document: { id: string; link: string; humanFilename?: string | null }) => {
    const download = () =>
      void downloadFile(document.link, document.humanFilename || 'Certificate of Origin.pdf');
    if (rxPrintEnabled && onSendToPrinter) {
      onSendToPrinter(CERTIFICATE_OF_ORIGIN_PRINT_KEY, [{ id: document.id, download }]);
    } else {
      download();
    }
  };

  const sending = sendingKey === CERTIFICATE_OF_ORIGIN_PRINT_KEY;
  return (
    <>
      <Button
        type="secondary"
        fullWidth
        className={['certificate-of-origin-button', className].filter(Boolean).join(' ')}
        iconBefore={<FiFileText />}
        disabled={!!sendingKey}
        {...{ onClick: () => setOpen(true) }}
      >
        {sending ? 'Sending…' : label || 'Certificate of Origin'}
      </Button>
      <Modal
        className="certificate-of-origin-modal"
        modalOpen={open}
        setModalOpen={(next) => {
          if (!next) setOpen(false);
        }}
      >
        {open && (
          <CertificateOfOriginForm
            orderId={orderId}
            companyId={companyId}
            rxPrintEnabled={rxPrintEnabled && !!onSendToPrinter}
            print={print}
            onClose={() => setOpen(false)}
            onDone={onDone}
          />
        )}
      </Modal>
    </>
  );
};

export default CertificateOfOriginButton;
